import { useContext, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import {
  AUTO_ACK_LIMITS,
  AUTO_ACK_PATIENT_DISCIPLINES,
  disciplineOfPatientKey,
  IAutoAckEvent,
  IAutoAckRule,
  IAutoAckRuleInput,
} from "@home-health-notify/domain";
import { IAutoAckCriteria } from "@home-health-notify/models";
import {
  OrganizationContext,
  UserDirectoryContext,
  useTranslation,
} from "hhn-commons";
import ConditionRow, { emptyCriteria, WHO_KEYS } from "./ConditionRow";
import {
  AndPill,
  BlockIndex,
  BlockTag,
  ConditionBlock,
  NarrowBox,
  OrBar,
  OrPill,
  Rule,
} from "./AutoAck.style";
import {
  buildPatientCriteria,
  selectableUsers,
  sortedEvents,
} from "./autoAckUtils";

type Props = {
  open: boolean;
  events: IAutoAckEvent[];
  rule?: IAutoAckRule;
  isSaving: boolean;
  onClose: () => void;
  onSave: (rule: IAutoAckRuleInput, ruleId?: string) => void;
};

// Lab events read differently from template submissions, so they are coloured
// apart here and on the rule cards.
const isLabEvent = (event: IAutoAckEvent) =>
  event.key.startsWith("patient:lab:");

const AutoAckRuleDialog = ({
  open,
  events,
  rule,
  isSaving,
  onClose,
  onSave,
}: Props) => {
  const { t } = useTranslation();
  const tNs = (key: string, options?: object) =>
    t(key, { ns: "autoAck", ...options });
  const { userDirectory } = useContext(UserDirectoryContext);
  const { selectedOrgUser } = useContext(OrganizationContext);

  const users = useMemo(
    () => selectableUsers(userDirectory.getUserDirectory()),
    [userDirectory],
  );
  const departments = useMemo(
    () => selectedOrgUser?.getDepartments() ?? [],
    [selectedOrgUser],
  );
  const eventOptions = useMemo(() => sortedEvents(events), [events]);

  const [selectedEvents, setSelectedEvents] = useState<IAutoAckEvent[]>(() =>
    eventOptions.filter(event => rule?.events.includes(event.key)),
  );
  const [groups, setGroups] = useState<IAutoAckCriteria[][]>(() =>
    rule?.userCriteria.length
      ? rule.userCriteria.map(group => group.map(criteria => ({ ...criteria })))
      : [[emptyCriteria(WHO_KEYS[0])]],
  );
  const [disciplines, setDisciplines] = useState<string[]>(() =>
    (rule?.patientCriteria ?? [])
      .flat()
      .map(criteria => disciplineOfPatientKey(criteria.key)),
  );
  const [showErrors, setShowErrors] = useState(false);

  const updateGroup = (groupIndex: number, next: IAutoAckCriteria[] | null) =>
    setGroups(previous =>
      next === null
        ? previous.filter((_, index) => index !== groupIndex)
        : previous.map((group, index) => (index === groupIndex ? next : group)),
    );

  const userCriteria = groups.filter(group => group.length);
  const patientCriteria = buildPatientCriteria(disciplines);

  const hasEmptyCondition = groups.some(group =>
    group.some(criteria => !(criteria.values ?? []).length),
  );
  const eventsError = selectedEvents.length === 0;
  const tooManyGroups = groups.length > AUTO_ACK_LIMITS.MAX_GROUPS_PER_RULE;

  const handleSave = () => {
    if (
      eventsError ||
      hasEmptyCondition ||
      !userCriteria.length ||
      tooManyGroups
    ) {
      setShowErrors(true);
      return;
    }
    onSave(
      {
        events: selectedEvents.map(event => event.key),
        userCriteria,
        patientCriteria,
      },
      rule?.id,
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{rule ? tNs("editRule") : tNs("addRule")}</DialogTitle>
      <DialogContent>
        <Typography variant="subtitle2">{tNs("whenHappens")}</Typography>
        <Typography variant="caption" color="text.secondary">
          {tNs("eventsHelper")}
        </Typography>
        <Autocomplete
          multiple
          disableCloseOnSelect
          size="small"
          sx={{ mt: 1 }}
          options={eventOptions}
          value={selectedEvents}
          onChange={(_, value) => setSelectedEvents(value)}
          groupBy={option => option.category}
          getOptionLabel={option => option.displayName}
          isOptionEqualToValue={(option, value) => option.key === value.key}
          renderOption={(props, option, { selected }) => (
            <li {...props} key={option.key}>
              <Checkbox checked={selected} sx={{ mr: 1 }} />
              <Typography variant="body2">{option.displayName}</Typography>
            </li>
          )}
          renderValue={(value, getItemProps) =>
            value.map((option, index) => (
              <Chip
                {...getItemProps({ index })}
                key={option.key}
                size="small"
                label={option.displayName}
                color={isLabEvent(option) ? "warning" : "primary"}
                variant={isLabEvent(option) ? "outlined" : "filled"}
              />
            ))
          }
          renderInput={params => (
            <TextField
              {...params}
              error={showErrors && eventsError}
              helperText={
                showErrors && eventsError ? tNs("eventsRequired") : undefined
              }
            />
          )}
        />

        <Box sx={{ mt: 2.5 }}>
          <Typography variant="subtitle2">{tNs("askThesePeople")}</Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 1 }}
          >
            {tNs("askHelper")}
          </Typography>

          {groups.map((group, groupIndex) => (
            <Box key={groupIndex}>
              {groupIndex > 0 && (
                <OrBar>
                  <Rule />
                  <OrPill>{tNs("or")}</OrPill>
                  <Rule />
                </OrBar>
              )}
              <ConditionBlock>
                {groups.length > 1 && (
                  <BlockTag>
                    <BlockIndex>{groupIndex + 1}</BlockIndex>
                    {groupIndex === 0 ? tNs("askThese") : tNs("orAlsoAsk")}
                    <Box sx={{ flex: 1 }} />
                    <Button
                      size="small"
                      color="error"
                      onClick={() => updateGroup(groupIndex, null)}
                    >
                      {tNs("remove")}
                    </Button>
                  </BlockTag>
                )}

                {group.map((criteria, criteriaIndex) => {
                  const row = (
                    <ConditionRow
                      criteria={criteria}
                      users={users}
                      departments={departments}
                      usedKeys={group
                        .filter((_, index) => index !== criteriaIndex)
                        .map(other => other.key)}
                      showErrors={showErrors}
                      onChange={next =>
                        updateGroup(
                          groupIndex,
                          group.map((other, index) =>
                            index === criteriaIndex ? next : other,
                          ),
                        )
                      }
                      onRemove={
                        group.length > 1
                          ? () =>
                              updateGroup(
                                groupIndex,
                                group.filter(
                                  (_, index) => index !== criteriaIndex,
                                ),
                              )
                          : undefined
                      }
                    />
                  );
                  return (
                    <Box key={criteriaIndex}>
                      {criteriaIndex > 0 && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            my: 1,
                          }}
                        >
                          <AndPill>{tNs("and")}</AndPill>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontStyle: "italic" }}
                          >
                            {tNs("onlyThoseWhoAre")}
                          </Typography>
                        </Box>
                      )}
                      {criteriaIndex > 0 ? <NarrowBox>{row}</NarrowBox> : row}
                    </Box>
                  );
                })}

                {group.length < WHO_KEYS.length && (
                  <Button
                    size="small"
                    onClick={() => {
                      const used = group.map(criteria => criteria.key);
                      const next = WHO_KEYS.find(key => !used.includes(key));
                      if (next)
                        updateGroup(groupIndex, [
                          ...group,
                          emptyCriteria(next),
                        ]);
                    }}
                  >
                    {tNs("narrowThisDown")}
                  </Button>
                )}
              </ConditionBlock>
            </Box>
          ))}

          <OrBar>
            <Rule />
            <Button
              variant="outlined"
              size="small"
              disabled={groups.length >= AUTO_ACK_LIMITS.MAX_GROUPS_PER_RULE}
              onClick={() =>
                setGroups(previous => [
                  ...previous,
                  [emptyCriteria(WHO_KEYS[0])],
                ])
              }
            >
              {tNs("alsoAskDifferentSet")}
            </Button>
            <Rule />
          </OrBar>
        </Box>

        <Box sx={{ mt: 2.5 }}>
          <Typography variant="subtitle2">
            {tNs("onlyCertainPatients")}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 1 }}
          >
            {tNs("patientsHelper")}
          </Typography>
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={AUTO_ACK_PATIENT_DISCIPLINES}
            value={disciplines}
            onChange={(_, value) => setDisciplines(value)}
            renderInput={params => (
              <TextField
                {...params}
                size="small"
                label={tNs("patientsStartingCareFor")}
                placeholder={disciplines.length ? undefined : tNs("anyPatient")}
                helperText={
                  disciplines.length > 1
                    ? tNs("allDisciplines", { count: disciplines.length })
                    : undefined
                }
              />
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{tNs("cancel")}</Button>
        <Button variant="contained" onClick={handleSave} disabled={isSaving}>
          {tNs("save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AutoAckRuleDialog;
